import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getAttribution } from "@/lib/attribution";
import { HoneypotField } from "@/components/HoneypotField";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { MathChallenge } from "@/components/MathChallenge";

const services = [
  "Photography",
  "Film & Video Production",
  "Post Production",
  "Photo Restoration",
  "Studio Rental",
  "Other",
];

interface ServiceContactFormProps {
  defaultService?: string;
}

const ServiceContactForm = ({ defaultService }: ServiceContactFormProps) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [service, setService] = useState(defaultService || "");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [fallbackQ, setFallbackQ] = useState("");
  const [fallbackA, setFallbackA] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    const recaptcha_token = await getRecaptchaToken("contact_service");
    const { data, error } = await supabase.functions.invoke("contact-submit", {
      body: {
        full_name: name, email, phone: phone || null,
        service: service || null, message,
        attribution: getAttribution(),
        honeypot, recaptcha_token,
        fallback_question: fallbackQ || undefined,
        fallback_answer: fallbackA || undefined,
      },
    });
    setLoading(false);
    if ((data as any)?.requires_fallback) {
      setFallbackQ((data as any).question);
      setFallbackA("");
      toast.message("Please complete the quick verification below.");
      return;
    }
    if (error || (data as any)?.error) {
      toast.error((data as any)?.error || "Could not send. Please try again.");
      return;
    }
    toast.success("Thanks — we sent you a confirmation email.");
    setName(""); setPhone(""); setEmail(""); setService(defaultService || ""); setMessage("");
    setHoneypot(""); setFallbackQ(""); setFallbackA("");
  };

  return (
    <section className="pb-20">
      <div className="container mx-auto px-4">
        <div className="bg-primary rounded-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 md:p-12">
            {/* Left – info */}
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl md:text-4xl font-display font-bold tracking-wider text-primary-foreground mb-4">
                PHOTO SERVICE QUESTIONS
              </h2>
              <p className="text-primary-foreground/80 font-body mb-8 max-w-md">
                If you have any questions, please do not hesitate to ask us.
                Please also call us or email us before visiting to make sure
                that you will be served with our best services.
              </p>
              <div className="space-y-3">
                <a
                  href="tel:7064787640"
                  className="flex items-center gap-3 text-primary-foreground font-mono hover:opacity-80 transition-opacity"
                >
                  706.478.7640 <Phone className="w-5 h-5" />
                </a>
                <a
                  href="mailto:info@bookmystudiospace.com"
                  className="flex items-center gap-3 text-primary-foreground font-mono hover:opacity-80 transition-opacity"
                >
                  info@bookmystudiospace.com <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Right – form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <HoneypotField value={honeypot} onChange={setHoneypot} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  placeholder="NAME*"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="bg-primary/70 border-0 text-primary-foreground placeholder:text-primary-foreground/60 font-body"
                />
                <Input
                  placeholder="PHONE*"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="bg-primary/70 border-0 text-primary-foreground placeholder:text-primary-foreground/60 font-body"
                />
              </div>
              <Input
                type="email"
                placeholder="EMAIL*"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-primary/70 border-0 text-primary-foreground placeholder:text-primary-foreground/60 font-body"
              />
              <Select value={service} onValueChange={setService}>
                <SelectTrigger className="bg-background border-0 text-foreground font-body">
                  <SelectValue placeholder="What Service Are You Inquiring About?" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                placeholder="HOW MAY WE HELP YOU?*"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={5}
                className="bg-primary/70 border-0 text-primary-foreground placeholder:text-primary-foreground/60 font-body resize-y"
              />
              {fallbackQ && (
                <MathChallenge question={fallbackQ} value={fallbackA} onChange={setFallbackA} />
              )}
              <Button
                type="submit"
                disabled={loading}
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-body font-bold px-8 uppercase tracking-wider"
              >
                {loading ? "Submitting…" : "Submit Now"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceContactForm;
